import { Controller } from "@hotwired/stimulus"

const METHODS = {
  authenticator: { subtext: "Enter the 6-digit code from your authenticator app", showResend: false },
  sms:           { subtext: "Enter the 6-digit code sent to your phone",           showResend: true  },
  email:         { subtext: "Enter the 6-digit code sent to your email",           showResend: true  },
}

export default class extends Controller {
  static targets = [
    "input", "inputRow", "error", "submitBtn",
    "methodBtn", "methodSubtext",
    "resendRow", "timerMsg", "timer", "resendBtn",
    "toast", "otpForm", "lockedState"
  ]

  connect() {
    this.method = "authenticator"
    this.attemptsLeft = 5
    this.resendSeconds = 60
    this.resendInterval = null

    // Focus first box
    this.inputTargets[0]?.focus()
  }

  disconnect() {
    clearInterval(this.resendInterval)
  }

  // ── Method switching ────────────────────────────────────────────────
  switchMethod(event) {
    const method = event.currentTarget.dataset.method
    this.method = method

    // Tab styles
    this.methodBtnTargets.forEach(btn => {
      const active = btn.dataset.method === method
      btn.className = btn.className
        .replace("bg-white text-slate-900 shadow-sm", "")
        .replace("text-slate-600 hover:text-slate-900", "")
        .trim()
      btn.classList.add(
        ...(active
          ? ["bg-white", "text-slate-900", "shadow-sm"]
          : ["text-slate-600", "hover:text-slate-900"])
      )
    })

    this.methodSubtextTarget.textContent = METHODS[method].subtext
    this.#resetInputs()

    if (METHODS[method].showResend) {
      this.resendRowTarget.classList.remove("hidden")
      this.#startResendTimer()
    } else {
      this.resendRowTarget.classList.add("hidden")
      clearInterval(this.resendInterval)
    }
  }

  // ── Input handling ──────────────────────────────────────────────────
  handleInput(event) {
    const input = event.currentTarget
    const idx   = parseInt(input.dataset.index)
    const val   = input.value.replace(/\D/, "")

    input.value = val
    this.errorTarget.classList.add("hidden")

    if (val && idx < 5) {
      this.inputTargets[idx + 1].focus()
    }

    this.#updateSubmitState()
    if (this.#isComplete()) this.#verify()
  }

  handleKeydown(event) {
    const input = event.currentTarget
    const idx   = parseInt(input.dataset.index)

    if (event.key === "Backspace") {
      event.preventDefault()
      if (!input.value && idx > 0) {
        this.inputTargets[idx - 1].focus()
        this.inputTargets[idx - 1].value = ""
      } else {
        input.value = ""
      }
      this.#updateSubmitState()
    } else if (event.key === "ArrowLeft"  && idx > 0) {
      this.inputTargets[idx - 1].focus()
    } else if (event.key === "ArrowRight" && idx < 5) {
      this.inputTargets[idx + 1].focus()
    }
  }

  handlePaste(event) {
    event.preventDefault()
    const digits = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6)
    if (digits.length !== 6) return

    this.inputTargets.forEach((input, i) => { input.value = digits[i] || "" })
    this.inputTargets[5].focus()
    this.#updateSubmitState()
    if (this.#isComplete()) this.#verify()
  }

  // ── Verify ──────────────────────────────────────────────────────────
  verify(event) {
    event?.preventDefault()
    this.#verify()
  }

  #verify() {
    const code = this.inputTargets.map(i => i.value).join("")
    if (code.length !== 6) return

    this.submitBtnTarget.disabled = true
    this.submitBtnTarget.innerHTML = `
      <svg class="mr-2 h-4 w-4 animate-spin inline" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
      </svg>Verifying...`

    // Demo: "123456" succeeds
    setTimeout(() => {
      if (code === "123456") {
        this.submitBtnTarget.innerHTML = `
          <svg class="mr-2 h-5 w-5 inline" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
          </svg>Verified!`
        this.inputTargets.forEach(i => {
          i.classList.add("border-emerald", "bg-emerald/5", "text-emerald")
          i.classList.remove("border-slate-200", "border-navy")
        })
      } else {
        this.attemptsLeft--
        if (this.attemptsLeft <= 0) {
          this.otpFormTarget.classList.add("hidden")
          this.lockedStateTarget.classList.remove("hidden")
        } else {
          this.errorTarget.textContent =
            `Invalid code. ${this.attemptsLeft} attempt${this.attemptsLeft !== 1 ? "s" : ""} remaining.`
          this.errorTarget.classList.remove("hidden")
          this.#shake()
        }
        this.submitBtnTarget.disabled = false
        this.submitBtnTarget.textContent = "Verify"
      }
    }, 1500)
  }

  // ── Resend ──────────────────────────────────────────────────────────
  resend() {
    this.#startResendTimer()
    this.#showToast("Code resent!")
  }

  #startResendTimer() {
    clearInterval(this.resendInterval)
    this.resendSeconds = 60
    this.resendBtnTarget.classList.add("hidden")
    this.timerMsgTarget.classList.remove("hidden")
    this.#updateTimer()

    this.resendInterval = setInterval(() => {
      this.resendSeconds--
      this.#updateTimer()
      if (this.resendSeconds <= 0) {
        clearInterval(this.resendInterval)
        this.timerMsgTarget.classList.add("hidden")
        this.resendBtnTarget.classList.remove("hidden")
      }
    }, 1000)
  }

  #updateTimer() {
    const m = Math.floor(this.resendSeconds / 60)
    const s = this.resendSeconds % 60
    this.timerTarget.textContent = `${m}:${String(s).padStart(2, "0")}`
  }

  #showToast(msg) {
    this.toastTarget.textContent = msg
    this.toastTarget.classList.remove("hidden")
    setTimeout(() => this.toastTarget.classList.add("hidden"), 3000)
  }

  // ── Helpers ─────────────────────────────────────────────────────────
  #isComplete() {
    return this.inputTargets.every(i => i.value !== "")
  }

  #updateSubmitState() {
    this.submitBtnTarget.disabled = !this.#isComplete()
  }

  #resetInputs() {
    this.inputTargets.forEach(i => {
      i.value = ""
      i.className = i.className
        .replace("border-emerald bg-emerald/5 text-emerald", "")
        .replace("border-red-500 bg-red-50 text-red-700", "")
        .trim()
    })
    this.errorTarget.classList.add("hidden")
    this.#updateSubmitState()
    this.inputTargets[0]?.focus()
  }

  #shake() {
    this.inputTargets.forEach(i => {
      i.classList.add("border-red-500", "bg-red-50", "text-red-700")
    })
    setTimeout(() => {
      this.#resetInputs()
    }, 500)
  }
}
